/** Crown Institute Academy — role dashboards for student, teacher_aide, parent.
 *  Minimal scaffolds that leverage existing curriculum + attempts endpoints.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PortalLayout, { usePortal } from "../components/PortalLayout";
import { portalApi } from "../lib/portalApi";
import { Button } from "../components/ui/button";
import {
  Crown, GraduationCap, ClipboardList, Users, Award, TrendingUp,
  CheckCircle2, AlertCircle, BookOpen, FileText, MessageSquare, Calendar,
} from "lucide-react";

const HERO = "/images/crown_institute_crest.png";

const Stat = ({ Icon, label, value, tone = "text-gold" }) => (
  <div className="glass p-4">
    <div className="flex items-center justify-between">
      <div className="text-[10px] uppercase tracking-[0.22em] text-steel">{label}</div>
      <Icon size={14} className={tone} />
    </div>
    <div className={`font-display text-2xl mt-2 ${tone}`}>{value}</div>
  </div>
);

// -------- Student Dashboard --------
const StudentInner = () => {
  const { me } = usePortal();
  const [sections, setSections] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [passedSet, setPassedSet] = useState(new Set());

  useEffect(() => {
    portalApi.curriculumSections().then(r => setSections(r.sections || []));
    portalApi.curriculumMyAttempts().then(r => {
      setAttempts(r.attempts || []);
      setPassedSet(new Set(r.passed_sections || []));
    });
  }, []);

  const progressPct = sections.length ? Math.round((passedSet.size / sections.length) * 100) : 0;
  const failing = attempts.filter(a => !a.passed).length;
  const recent = attempts.slice(0, 5);

  return (
    <div className="space-y-6" data-testid="student-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <img src={HERO} alt="" className="h-16" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-gold flex items-center gap-2">
              <Crown size={12} /> Crown Institute Academy
            </div>
            <h1 className="font-display text-3xl md:text-4xl mt-1">Welcome, {me?.name?.split(" ")[0] || "Student"}.</h1>
            <p className="text-cream-dim text-sm mt-1">Your learning journey at a glance.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat Icon={BookOpen}     label="Courses in progress" value={sections.length} tone="text-electric" />
        <Stat Icon={CheckCircle2} label="Sections passed"     value={passedSet.size}  tone="text-[color:var(--auditiq-success)]" />
        <Stat Icon={TrendingUp}   label="Overall progress"    value={`${progressPct}%`} />
        <Stat Icon={AlertCircle}  label="Remediation needed"  value={failing} tone="text-[color:var(--auditiq-warning)]" />
      </div>

      <div className="glass p-5">
        <div className="flex items-center justify-between text-sm">
          <div className="text-cream">Curriculum progress</div>
          <span className="text-gold font-display text-lg">{progressPct}%</span>
        </div>
        <div className="mt-3 h-2 w-full bg-navy-deep rounded overflow-hidden border border-gold-dim/20">
          <div className="h-full bg-gradient-to-r from-electric to-gold transition-all" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="mt-4 flex gap-2 flex-wrap">
          <Link to="/portal/curriculum"><Button className="btn-gold h-10 px-4 text-xs" data-testid="student-continue-learning">
            <GraduationCap size={14} className="mr-2" /> Continue Learning
          </Button></Link>
          <Link to="/portal/certification"><Button className="btn-ghost-gold h-10 px-4 text-xs">
            <Award size={14} className="mr-2" /> My Certificate
          </Button></Link>
        </div>
      </div>

      <div className="glass p-5">
        <div className="text-[11px] uppercase tracking-[0.2em] text-steel mb-3">Recent attempts</div>
        {recent.length ? (
          <div className="space-y-2">
            {recent.map(a => (
              <div key={a.id} className="flex items-center justify-between text-sm" data-testid={`student-attempt-${a.id}`}>
                <div>
                  <div className="text-cream">{a.section_title}</div>
                  <div className="text-[10px] text-steel">{new Date(a.attempted_at).toLocaleString()}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gold font-mono text-xs">{a.score_out_of_10}/10</span>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
                    a.passed ? "text-[color:var(--auditiq-success)] border-[color:var(--auditiq-success)]"
                             : "text-[color:var(--auditiq-critical)] border-[color:var(--auditiq-critical)]"
                  }`}>{a.passed ? "Pass" : "Retake"}</span>
                </div>
              </div>
            ))}
          </div>
        ) : <div className="text-cream-dim text-sm italic">No attempts yet. Visit Curriculum to begin.</div>}
      </div>
    </div>
  );
};

export const StudentDashboard = () => <PortalLayout><StudentInner /></PortalLayout>;


// -------- Teacher's Aide Dashboard --------
const TeacherAideInner = () => {
  const { me } = usePortal();
  const [sections, setSections] = useState([]);

  useEffect(() => {
    portalApi.curriculumSections().then(r => setSections(r.sections || []));
  }, []);

  return (
    <div className="space-y-6" data-testid="teacher-aide-dashboard">
      <div className="flex items-center gap-4">
        <img src={HERO} alt="" className="h-16" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-gold flex items-center gap-2">
            <Crown size={12} /> Crown Institute Academy
          </div>
          <h1 className="font-display text-3xl md:text-4xl mt-1">Teacher's Aide Dashboard</h1>
          <p className="text-cream-dim text-sm mt-1">Welcome, {me?.name}. Review student performance below.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat Icon={Users}       label="Students on roster"       value="12" tone="text-electric" />
        <Stat Icon={ClipboardList} label="Assignments to grade"   value="7"  tone="text-gold" />
        <Stat Icon={AlertCircle} label="Students below 9.2"       value="3"  tone="text-[color:var(--auditiq-warning)]" />
        <Stat Icon={Calendar}    label="Internship logs pending"  value="2"  tone="text-cream" />
      </div>

      <div className="glass p-5">
        <div className="text-[11px] uppercase tracking-[0.2em] text-steel mb-3">Student Roster (demo view)</div>
        <p className="text-cream-dim text-sm">
          Full roster management, assignment grading, and internship log approval will open in <span className="text-gold">Phase 2</span>. For now this dashboard reads the shared curriculum and displays section structure.
        </p>
        <div className="mt-4 space-y-2">
          {sections.slice(0, 5).map(s => (
            <div key={s.id} className="flex items-center justify-between text-sm border-b border-gold-dim/10 pb-2">
              <div>
                <div className="text-cream">Section {s.order} — {s.title}</div>
                <div className="text-[10px] text-steel">{s.questions} Q · pass {s.pass_score}/10</div>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-electric">{s.lessons?.length || 0} lessons</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="glass p-5">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-gold">
            <MessageSquare size={12} /> Communication
          </div>
          <p className="text-cream-dim text-sm mt-2">Message parents, post announcements, and schedule office hours. Available in Phase 2.</p>
        </div>
        <div className="glass p-5">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-gold">
            <FileText size={12} /> Reporting
          </div>
          <p className="text-cream-dim text-sm mt-2">Generate student progress reports, internship verification forms, and weekly summaries.</p>
        </div>
      </div>
    </div>
  );
};

export const TeacherAideDashboard = () => <PortalLayout><TeacherAideInner /></PortalLayout>;


// -------- Parent Dashboard --------
const ParentInner = () => {
  const { me } = usePortal();
  const children = me?.children || [];

  return (
    <div className="space-y-6" data-testid="parent-dashboard">
      <div className="flex items-center gap-4">
        <img src={HERO} alt="" className="h-16" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-gold flex items-center gap-2">
            <Crown size={12} /> Crown Institute Academy
          </div>
          <h1 className="font-display text-3xl md:text-4xl mt-1">Parent Dashboard</h1>
          <p className="text-cream-dim text-sm mt-1">Welcome, {me?.name}. Monitor your child&apos;s progress.</p>
        </div>
      </div>

      <div className="glass p-5">
        <div className="text-[11px] uppercase tracking-[0.2em] text-steel">Children linked to your account</div>
        {children.length ? (
          <ul className="mt-3 space-y-2">
            {children.map(c => (
              <li key={c} className="flex items-center gap-3 text-cream" data-testid={`parent-child-${c}`}>
                <div className="w-10 h-10 rounded-full bg-gold/20 border border-gold flex items-center justify-center">
                  <GraduationCap size={16} className="text-gold" />
                </div>
                <div>
                  <div className="font-mono text-xs text-gold">{c}</div>
                  <div className="text-[10px] text-steel">Crown Academy Student</div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-cream-dim text-sm mt-2">No children linked yet. Please contact the Academy office.</p>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat Icon={TrendingUp}  label="Overall progress"      value="—" tone="text-electric" />
        <Stat Icon={CheckCircle2} label="Exam scores avg"      value="—" tone="text-[color:var(--auditiq-success)]" />
        <Stat Icon={AlertCircle} label="Remediation alerts"    value="0" tone="text-[color:var(--auditiq-warning)]" />
        <Stat Icon={Calendar}    label="Internship hours"      value="—" tone="text-gold" />
      </div>

      <div className="glass p-5">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-gold mb-2">
          <MessageSquare size={12} /> Messages
        </div>
        <p className="text-cream-dim text-sm">
          Parent-teacher messaging opens in <span className="text-gold">Phase 2</span>. In the meantime, use the Academy contact form to reach our office.
        </p>
      </div>
    </div>
  );
};

export const ParentDashboard = () => <PortalLayout><ParentInner /></PortalLayout>;
